import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import { useMount } from '@fluentui/react-hooks'
import { useDispatch } from 'react-redux'
import { MenuItemKey } from '../../components/MainMenu'
import { AppAction } from '../../config/appConfig'
import { EmailItem } from '../../components/List/Email/EmailList'
import useUser from '../../hooks/useUser'
import EmailSearchSelectList, { mapMessages } from '../../components/Email/EmailSearchSelectList'
import { Message } from '@microsoft/microsoft-graph-types'
import useEntity from '../../hooks/useEntity'
import { Destination, IGraphArrayPayload } from '../../context/AxiosContext'
import { FILE_REFERENCE_NUMBER_PROP } from '../../helpers/referenceNumberHelper'
import { compareDateTime } from '../../helpers/dateHelper'
import GlobalEmailToolbar from '../../components/Toolbar/Email/GlobalEmailToolbar'
import { makeStyles, MessageBar, shorthands, tokens } from '@fluentui/react-components'
import { getIntervalDate, Interval } from '../../components/Toolbar/IntervalToolbarMenu'
import { useTranslation } from 'react-i18next'
import useHost from '../../hooks/useHost'

function getUnprocessedEmailsFromMailFolderConfig(mailFolder: string, interval: () => Interval) {
    const dateInterval = getIntervalDate(interval())
    const dateFilter = dateInterval === undefined || Interval.All === interval() ? '' : `and sentDateTime ge ${dateInterval.toISODate()} `

    return {
        url: `/mailFolders('${mailFolder}')/messages`,
        baseURL: Destination.Email,
        headers: { SuppressErrors: 'true', 'If-None-Match': null, Prefer: 'IdType="ImmutableId"' },
        params: {
            $select:
                'id,internetMessageId,conversationId,subject,bodyPreview,webLink,hasAttachments,sender,toRecipients,receivedDateTime,sentDateTime',
            $filter: `singleValueExtendedProperties/Any(ep: ep/id eq '${FILE_REFERENCE_NUMBER_PROP}' and ep/value eq null) and isDraft eq false ${dateFilter}`,
            $orderby: 'sentDateTime desc',
            $top: 20,
        },
    }
}

const useStyles = makeStyles({
    alert: {
        display: 'grid',
        ...shorthands.margin(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    },
    nowrap: {
        whiteSpace: 'normal',
    },
})

export default function GlobalEmailFilingOverview(): ReactElement {
    const dispatch = useDispatch()
    const styles = useStyles()
    const { t: translate } = useTranslation()

    useMount(() => {
        dispatch({ type: AppAction.SetBreadcrumbs, payload: [MenuItemKey.Emails] })
    })

    const host = useHost()
    const { currentMailbox, currentEmail } = useUser()
    const [interval, setInterval] = useState<Interval>(Interval.Month)
    const [isLoading, setIsLoading] = useState<boolean>(true)

    const [originalInboxMessages, , resetInboxMessages] = useEntity<IGraphArrayPayload<Message>>(
        () => getUnprocessedEmailsFromMailFolderConfig('inbox', () => interval),
        interval,
    )
    const [originalSentItemsMessages, , resetSentItemsMessages] = useEntity<IGraphArrayPayload<Message>>(
        () => getUnprocessedEmailsFromMailFolderConfig('sentitems', () => interval),
        interval,
    )

    const originalMessages: EmailItem[] = useMemo<EmailItem[]>(() => {
        if (originalInboxMessages.loaded && originalSentItemsMessages.loaded) {
            setIsLoading(false)
            return [
                ...mapMessages(originalInboxMessages.data.value || [], currentMailbox, currentEmail, host, Destination.Email),
                ...mapMessages(originalSentItemsMessages.data.value || [], currentMailbox, currentEmail, host, Destination.Email),
            ].sort((a: EmailItem, b: EmailItem) => compareDateTime(b.date, a.date))
        }
        setIsLoading(false)
        return []
    }, [originalInboxMessages, originalSentItemsMessages])

    return (
        <div>
            {originalInboxMessages.loaded && (
                <div className={styles.alert}>
                    <MessageBar intent="info">{translate('email.filing.info')}</MessageBar>
                </div>
            )}
            <EmailSearchSelectList
                emailMailbox={currentMailbox}
                interval={interval}
                setInterval={setInterval}
                messages={originalInboxMessages}
                items={originalMessages}
                hasItems={originalMessages?.length !== 0}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                reset={() => {
                    resetInboxMessages()
                    resetSentItemsMessages()
                }}
                showLocation={false}
                toolbar={GlobalEmailToolbar}
            />
        </div>
    )
}
